import { computed, watch, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import {
  ReportEvents,
  LIGHTING_TARGET,
  type KeyboardLightingTarget,
  type LightingCapability,
  type LightingConfigData,
  type LightingDomainType,
  type LightingEffectDefinition,
  type RGB,
  type RGBGroup,
  type keyMapLighting,
  type keyRgbItem,
  type keyboardLightingCustomConf,
} from '@rapoo/device-sdk'
import { useDeviceStore } from '@/stores'
import { defaultReportError } from '@/composables/useErrorLogger'

/**
 * 键盘灯光设备 I/O + 共用工具。
 *
 * - useLightingSDK：转发 features.keyboardLighting / 上报事件
 * - beginLatest / createLatestCommitter / useLightingCapability / useLightingLifecycle：领域 hook 共用
 */

/** 最新任务守卫：并发时只允许最后一次继续写状态 */
export function beginLatest(counter: { value: number }) {
  const token = ++counter.value
  return {
    alive: () => token === counter.value,
  }
}

export type LatestCommitContext = {
  /** 是否已有更新的同 key 载荷在等 */
  hasNewer: () => boolean
  /**
   * 连击静默：本次载荷若紧跟上一次提交（间隔 < burstGapMs），最多等 settleMs 看有没有更新值，
   * 期间来了就提前结束并返回 true；孤立提交立即返回 false，不加任何延迟。
   *
   * 没有它，一轮「写 + 重下发」耗时和人手连点间隔相近时，槽位里永远只积 1 个待写值，
   * 什么都丢不掉——设备会滞后地把每个值都亮一遍，停手后还继续回放最后一两次。
   */
  settle: () => Promise<boolean>
}

export type LatestCommitterOptions = {
  /** 连击静默上限（ms），默认 300 */
  settleMs?: number
  /** 与上一次提交间隔小于该值视为连击（ms），默认 500 */
  burstGapMs?: number
}

/**
 * 按 key 合并写入：同一 key 已有任务在跑时，新载荷只覆盖待写槽位并立即返回；
 * 进行中的任务结束后只再写最后那份，中间值全部丢弃。
 *
 * 用于滑块松手这类「中间值没意义、只要最终值落到设备」的提交：
 * 若直接串进 FIFO 写链，设备会把每个中间值依次写一遍、再逐个回读进 UI。
 * 任务通过 `ctx.settle()` / `ctx.hasNewer()` 判断自己是不是最后一轮，中间轮省掉重下发 / 回读。
 * 首个调用方的 Promise 会等到该 key 的待写槽位清空才结算。
 */
export function createLatestCommitter<K, P>(
  run: (key: K, payload: P, ctx: LatestCommitContext) => Promise<void>,
  options: LatestCommitterOptions = {},
) {
  const { settleMs = 300, burstGapMs = 500 } = options
  const pending = new Map<K, { payload: P; burst: boolean }>()
  const active = new Set<K>()
  const lastEnqueuedAt = new Map<K, number>()
  /** settle 期间的唤醒器：新载荷入槽时提前结束等待 */
  const wakers = new Map<K, () => void>()

  return async (key: K, payload: P): Promise<void> => {
    const now = Date.now()
    const prev = lastEnqueuedAt.get(key)
    lastEnqueuedAt.set(key, now)
    pending.set(key, { payload, burst: prev !== undefined && now - prev < burstGapMs })
    wakers.get(key)?.()

    if (active.has(key)) return
    active.add(key)
    try {
      for (;;) {
        const entry = pending.get(key)
        if (!entry) break
        pending.delete(key)

        const hasNewer = () => pending.has(key)
        const settle = async () => {
          if (!entry.burst || settleMs <= 0 || hasNewer()) return hasNewer()
          await new Promise<void>((resolve) => {
            const finish = () => {
              clearTimeout(timer)
              wakers.delete(key)
              resolve()
            }
            const timer = setTimeout(finish, settleMs)
            wakers.set(key, finish)
          })
          return hasNewer()
        }

        await run(key, entry.payload, { hasNewer, settle })
      }
    } finally {
      active.delete(key)
    }
  }
}

/** 灯光 capability 与各灯区是否存在 */
export function useLightingCapability() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const lightingCapability = computed(
    () =>
      currentDevice.value?.manifest.capabilities.find((item) => item.type === 'lighting') as
        | LightingCapability
        | undefined,
  )

  const hasBacklightSwitch = computed(
    () => lightingCapability.value?.config.backlightSwitch ?? false,
  )

  const hasSideLighting = computed(() =>
    Boolean(
      lightingCapability.value?.config.effects.some(
        (effect) => effect.target === LIGHTING_TARGET.Side,
      ),
    ),
  )

  const hasIndicator = computed(() =>
    Boolean(
      lightingCapability.value?.config.effects.some(
        (effect) => effect.target === LIGHTING_TARGET.Indicator,
      ),
    ),
  )

  const hasAudioRhythm = computed(() =>
    Boolean(
      lightingCapability.value?.config.effects.some(
        (effect) => effect.target === LIGHTING_TARGET.AudioRhythm,
      ),
    ),
  )

  return {
    currentDevice,
    lightingCapability,
    hasBacklightSwitch,
    hasSideLighting,
    hasIndicator,
    hasAudioRhythm,
  }
}

type LightingDevice = NonNullable<ReturnType<typeof useDeviceStore>['currentDevice']>

export type UseLightingLifecycleOptions = {
  /** 编排层应传 false。默认 true */
  autoInit?: boolean
  onDevice?: (device: LightingDevice) => void
  onDeviceLeave?: (device: LightingDevice) => void
  reset?: () => void
  init?: (opts?: { force?: boolean }) => void | Promise<void>
  onInvalidate?: () => void
}

/** 设备 watch / autoInit / unmount；亮度/速度/灯效上报只在 useKeyLighting 注册 */
export function useLightingLifecycle(options: UseLightingLifecycleOptions = {}) {
  const { autoInit = true, onDevice, onDeviceLeave, reset, init, onInvalidate } = options
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  let prevDeviceId: string | undefined

  watch(
    () => currentDevice.value,
    (newDevice, oldDevice) => {
      if (oldDevice && prevDeviceId === oldDevice.id) {
        onDeviceLeave?.(oldDevice)
        prevDeviceId = undefined
      }
      if (newDevice) {
        onDevice?.(newDevice)
        prevDeviceId = newDevice.id
        if (autoInit) {
          reset?.()
          void init?.({ force: true })
        }
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    onInvalidate?.()
    if (currentDevice.value) onDeviceLeave?.(currentDevice.value)
  })

  return { currentDevice }
}

export function useLightingSDK() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const getLighting = () => {
    const lighting = currentDevice.value?.features.keyboardLighting
    if (!lighting) {
      console.warn('Current device does not support keyboard lighting')
      return undefined
    }
    return lighting
  }

  const getEffects = async (
    target?: KeyboardLightingTarget,
  ): Promise<LightingEffectDefinition[]> => {
    const lighting = getLighting()
    if (!lighting) return []
    try {
      return await lighting.getEffects(target)
    } catch (error) {
      console.error('Failed to get lighting effects:', error)
      defaultReportError(`Failed to get lighting effects: ${JSON.stringify(error)}`)
      return []
    }
  }

  const getConfig = async (
    domain: LightingDomainType,
    index: number,
  ): Promise<LightingConfigData | undefined> => {
    const lighting = getLighting()
    if (!lighting) return undefined
    try {
      return await lighting.getConfig(domain, index)
    } catch (error) {
      console.error('Failed to get lighting config:', error)
      defaultReportError(`Failed to get lighting config: ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const setConfig = async (
    domain: LightingDomainType,
    index: number,
    config: Partial<LightingConfigData>,
  ): Promise<void> => {
    const lighting = getLighting()
    if (!lighting) return
    try {
      await lighting.setConfig(domain, index, config)
    } catch (error) {
      console.error('Failed to set lighting config:', error)
      defaultReportError(`Failed to set lighting config: ${JSON.stringify(error)}`)
      throw error
    }
  }

  const getProperty = async (
    domain: LightingDomainType,
    index: number,
    key: string,
  ): Promise<number | boolean | RGB | RGBGroup | undefined> => {
    const lighting = getLighting()
    if (!lighting) return undefined
    try {
      return await lighting.getProperty(domain, index, key)
    } catch (error) {
      console.error(`Failed to get lighting property "${key}":`, error)
      defaultReportError(`Failed to get lighting property "${key}": ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const setProperty = async (
    domain: LightingDomainType,
    index: number,
    key: string,
    value: number | boolean | RGB | RGBGroup,
  ): Promise<void> => {
    const lighting = getLighting()
    if (!lighting) return
    try {
      await lighting.setProperty(domain, index, key, value)
    } catch (error) {
      console.error(`Failed to set lighting property "${key}":`, error)
      defaultReportError(`Failed to set lighting property "${key}": ${JSON.stringify(error)}`)
      throw error
    }
  }

  const getKeysColorStream = (
    domain: LightingDomainType,
    index: number,
  ): AsyncIterable<keyRgbItem> | undefined => {
    const lighting = getLighting()
    if (!lighting?.getKeysColorStream) return undefined
    return lighting.getKeysColorStream(domain, index)
  }

  const setKeysColor = async (
    domain: LightingDomainType,
    keyMap: keyMapLighting,
    index: number,
  ): Promise<void> => {
    const lighting = getLighting()
    if (!lighting) return
    try {
      await lighting.setKeysColor(domain, keyMap, index)
    } catch (error) {
      console.error('Failed to set keys color:', error)
      defaultReportError(`Failed to set keys color: ${JSON.stringify(error)}`)
      throw error
    }
  }

  const getCurrentEffect = async (): Promise<number | undefined> => {
    const lighting = getLighting()
    if (!lighting) return undefined
    try {
      return await lighting.getCurrentEffect()
    } catch (error) {
      console.error('Failed to get current effect:', error)
      defaultReportError(`Failed to get current effect: ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const setCurrentEffect = async (effect: number): Promise<void> => {
    const lighting = getLighting()
    if (!lighting) return
    try {
      await lighting.setCurrentEffect(effect)
    } catch (error) {
      console.error('Failed to set current effect:', error)
      defaultReportError(`Failed to set current effect: ${JSON.stringify(error)}`)
      throw error
    }
  }

  const getSideEffect = async (): Promise<number | undefined> => {
    const lighting = getLighting()
    if (!lighting?.getSideEffect) return undefined
    try {
      return await lighting.getSideEffect()
    } catch (error) {
      console.error('Failed to get side effect:', error)
      defaultReportError(`Failed to get side effect: ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const setSideEffect = async (effect: number): Promise<void> => {
    const lighting = getLighting()
    if (!lighting?.setSideEffect) return
    try {
      await lighting.setSideEffect(effect)
    } catch (error) {
      console.error('Failed to set side effect:', error)
      defaultReportError(`Failed to set side effect: ${JSON.stringify(error)}`)
      throw error
    }
  }

  const getBacklightSwitch = async (): Promise<number | undefined> => {
    const lighting = getLighting()
    if (!lighting) return undefined
    try {
      return await lighting.getBacklightSwitch()
    } catch (error) {
      console.error('Failed to get backlight switch:', error)
      defaultReportError(`Failed to get backlight switch: ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const setBacklightSwitch = async (enabled: boolean): Promise<void> => {
    const lighting = getLighting()
    if (!lighting) return
    try {
      await lighting.setBacklightSwitch(enabled)
    } catch (error) {
      console.error('Failed to set backlight switch:', error)
      defaultReportError(`Failed to set backlight switch: ${JSON.stringify(error)}`)
      throw error
    }
  }

  const getSideLightDynamicEnable = async (): Promise<boolean | undefined> => {
    const lighting = getLighting()
    if (!lighting?.getSideLightDynamicEnable) return undefined
    try {
      return await lighting.getSideLightDynamicEnable()
    } catch (error) {
      console.error('Failed to get side light enable:', error)
      defaultReportError(`Failed to get side light enable: ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const setSideLightDynamicEnable = async (enable: boolean): Promise<void> => {
    const lighting = getLighting()
    if (!lighting?.setSideLightDynamicEnable) return
    try {
      await lighting.setSideLightDynamicEnable(enable)
    } catch (error) {
      console.error('Failed to set side light enable:', error)
      defaultReportError(`Failed to set side light enable: ${JSON.stringify(error)}`)
      throw error
    }
  }

  const createCustomEffect = async (
    config: keyboardLightingCustomConf,
    index: number,
  ): Promise<void> => {
    const lighting = getLighting()
    if (!lighting) return
    try {
      await lighting.createCustomEffect(config, index)
    } catch (error) {
      console.error('Failed to create custom effect:', error)
      defaultReportError(`Failed to create custom effect: ${JSON.stringify(error)}`)
      throw error
    }
  }

  const deleteCustomEffect = async (index: number): Promise<number | undefined> => {
    const lighting = getLighting()
    if (!lighting) return undefined
    try {
      return await lighting.deleteCustomEffect(index)
    } catch (error) {
      console.error('Failed to delete custom effect:', error)
      defaultReportError(`Failed to delete custom effect: ${JSON.stringify(error)}`)
      throw error
    }
  }

  type ReportDevice = NonNullable<typeof currentDevice.value>

  const onBrightnessChanged = (
    handler: (blLevel: number) => void,
    device: ReportDevice | null | undefined = currentDevice.value,
  ) => {
    device?.on(ReportEvents.BrightnessChanged, handler)
  }

  const offBrightnessChanged = (
    handler: (blLevel: number) => void,
    device: ReportDevice | null | undefined = currentDevice.value,
  ) => {
    device?.off(ReportEvents.BrightnessChanged, handler)
  }

  const onSpeedChanged = (
    handler: (blSpeed: number) => void,
    device: ReportDevice | null | undefined = currentDevice.value,
  ) => {
    device?.on(ReportEvents.SpeedChanged, handler)
  }

  const offSpeedChanged = (
    handler: (blSpeed: number) => void,
    device: ReportDevice | null | undefined = currentDevice.value,
  ) => {
    device?.off(ReportEvents.SpeedChanged, handler)
  }

  /** 硬件侧灯效切换（FN 灯效切换键等）上报，payload 为 blMode */
  const onEffectChanged = (
    handler: (blMode: number) => void,
    device: ReportDevice | null | undefined = currentDevice.value,
  ) => {
    device?.on(ReportEvents.EffectChanged, handler)
  }

  const offEffectChanged = (
    handler: (blMode: number) => void,
    device: ReportDevice | null | undefined = currentDevice.value,
  ) => {
    device?.off(ReportEvents.EffectChanged, handler)
  }

  const hasSideEffectApi = () => {
    const lighting = getLighting()
    return Boolean(lighting?.getSideEffect && lighting?.setSideEffect)
  }

  const hasSideLightEnableApi = () => {
    const lighting = getLighting()
    return Boolean(lighting?.getSideLightDynamicEnable && lighting?.setSideLightDynamicEnable)
  }

  const hasKeysColorStreamApi = () => Boolean(getLighting()?.getKeysColorStream)

  return {
    getLighting,
    getEffects,
    getConfig,
    setConfig,
    getProperty,
    setProperty,
    getKeysColorStream,
    setKeysColor,
    getCurrentEffect,
    setCurrentEffect,
    getSideEffect,
    setSideEffect,
    getBacklightSwitch,
    setBacklightSwitch,
    getSideLightDynamicEnable,
    setSideLightDynamicEnable,
    createCustomEffect,
    deleteCustomEffect,
    onBrightnessChanged,
    offBrightnessChanged,
    onSpeedChanged,
    offSpeedChanged,
    onEffectChanged,
    offEffectChanged,
    hasSideEffectApi,
    hasSideLightEnableApi,
    hasKeysColorStreamApi,
  }
}
